import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { propTypes } from '../../util/types';

import { sendVerificationEmail, hasCurrentUserErrors, currentUserShowSuccess } from '../../ducks/user.duck';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { nativeUpdateProfileApi } from '../../util/api';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

/**
 * Topbar container component, which is connected to Redux Store.
 * @component
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const TopbarContainerComponent = props => {
  const { notificationCount = 0, ...rest } = props;

  return <Topbar notificationCount={notificationCount} {...rest} />;
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount: providerNotificationCount,
    customerUserNotificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  // Topbar needs stripe account info for Become a Coach flow
  const { stripeAccount } = state.stripeConnectAccount || {};
  const notificationCount = (providerNotificationCount || 0) + (customerUserNotificationCount || 0);
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    stripeAccount,
    notificationCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onBecomeCoach: () =>
    dispatch((dispatch, getState) => {
      const { currentUser } = getState().user;
      if (!currentUser) return Promise.resolve();

      const nextPublicData = {
        ...currentUser.attributes.profile?.publicData,
        coach: true,
      };

      // Update Redux store immediately with coach: true
      const updatedUser = {
        ...currentUser,
        attributes: {
          ...currentUser.attributes,
          profile: {
            ...currentUser.attributes.profile,
            publicData: nextPublicData,
          },
        },
      };
      dispatch(currentUserShowSuccess(updatedUser));

      // Then call the API to persist the change. Phase 5 (native session):
      // publicData is replaced wholesale server-side, not deep-merged, so
      // the full nextPublicData object (not just { coach: true }) has to
      // be sent -- same reason the optimistic Redux update above already
      // spreads the existing publicData rather than overwriting it.
      return nativeUpdateProfileApi({ publicData: nextPublicData }).catch(e => {
        console.error(e);
        // Revert the Redux store if API call fails
        dispatch(currentUserShowSuccess(currentUser));
      });
    }),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(TopbarContainerComponent);

export default TopbarContainer;
